import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Radio } from 'lucide-react'
import { EmptyState } from '../cc/EmptyState'
import { Panel } from '../cc/Panel'
import { EYEBROW } from '../cc/tokens'
import { fetchBusEta, fetchBusGeotags, fetchBusRouteDetail, type BusGeotagEvent, type BusRouteEta } from './useBusApi'
import {
  STATUS_RESOLVED,
  STATUS_RESOLVED_GEOMETRIC,
  hasOutshed,
  nextBusRouteStop,
  resolveVehicle,
} from './nagpurRouteResolve'
import type { BusBoardRow, BusRouteDetail, BusRouteStop, BusRouteSummary } from './types'

/** Direct, honest labels for every non-RESOLVED status -- see
 *  nagpurRouteResolve.ts's own docstring. The panel must say WHY next-stop/ETA aren't
 *  showing, not just omit them silently (confirmed live 2026-11-04: 230/250 of
 *  today's board falls into one of these, not the RESOLVED case below). */
const STATUS_LABEL: Record<string, string> = {
  unmapped: 'This route has no stop data loaded yet.',
  depot_mismatch: 'This duty\'s route does not belong to this bus\'s home depot — needs a roster fix.',
  ambiguous_direction: 'This route runs Up and Down and today\'s duty data does not say which — not shown.',
  ambiguous_depot_and_direction: 'Neither depot nor direction is resolvable for this duty — not shown.',
  unparsed_schedule_code: 'No readable schedule for this bus today.',
}

export function NagpurBusDetailPanel({
  vehicle, routes,
}: {
  vehicle: BusBoardRow | null
  routes: BusRouteSummary[]
}): JSX.Element {
  const resolution = useMemo(
    () =>
      vehicle
        ? resolveVehicle(vehicle.schedule_code, vehicle.home_depot_id, routes, {
            routeId: vehicle.geometry_resolved_route_id,
            direction: vehicle.geometry_resolved_direction,
          })
        : null,
    [vehicle, routes],
  )
  const isResolved = resolution?.status === STATUS_RESOLVED || resolution?.status === STATUS_RESOLVED_GEOMETRIC
  // Duty resolving to a route_id is necessary but not sufficient -- see hasOutshed's own
  // comment. Gating here (not deeper, per-derived-value) means routeDetail/nextStop/eta
  // below all cascade to null for free, and the "Route"/"Next stop"/"ETA" block simply
  // has nothing to render until the bus actually leaves the depot.
  const resolvedRouteId = isResolved && vehicle && hasOutshed(vehicle.status) ? resolution!.routeId : null

  const [routeDetail, setRouteDetail] = useState<BusRouteDetail | null>(null)
  useEffect(() => {
    if (!resolvedRouteId) {
      setRouteDetail(null)
      return
    }
    let alive = true
    fetchBusRouteDetail(resolvedRouteId)
      .then((d) => { if (alive) setRouteDetail(d) })
      .catch(() => { if (alive) setRouteDetail(null) })
    return () => { alive = false }
  }, [resolvedRouteId])

  const nextStop: BusRouteStop | null = useMemo(() => {
    if (!vehicle || !routeDetail || vehicle.lat == null || vehicle.lon == null) return null
    return nextBusRouteStop(
      routeDetail.stops,
      { lat: vehicle.lat, lon: vehicle.lon },
      { moving: vehicle.speed_kph != null ? vehicle.speed_kph > 3 : undefined },
    )
  }, [vehicle, routeDetail])

  // Last stop in sequence order stands in for "destination" -- a route_id is already
  // one resolved leg (see nagpurRouteResolve.ts's toMbmtStop comment), so its own
  // final stop is the only well-defined endpoint, loop or not.
  const destinationStop: BusRouteStop | null = useMemo(() => {
    if (!routeDetail?.stops.length) return null
    return routeDetail.stops.reduce((a, b) => (b.sequence_no > a.sequence_no ? b : a))
  }, [routeDetail])

  // Direct instruction: Google Directions is a metered, non-free external call and
  // must fire ONLY when a bus is clicked -- exactly once per selection, no periodic
  // re-poll at all, not even on the earlier 45s interval. (That interval version had
  // its own confirmed-live bug on top: the board this panel's `vehicle` prop comes
  // from polls every FLEET_POLL_MS -- 15s, NagpurBusOpsShell.tsx -- producing a fresh
  // object on every tick; vehicle.lat/vehicle.lon sitting directly in the effect's own
  // dependency array meant it tore down and rebuilt, firing an immediate call, on
  // every one of THOSE 15s ticks too -- Google Directions was being hit roughly every
  // 15s per open panel, not 45s. Gone now, not just fixed: no interval, no lat/lon in
  // the deps at all, and this effect's own deps use vehicle?.vehicle_id (a primitive),
  // not the `vehicle` object itself, so an ordinary 15s board-poll tick with the SAME
  // plate selected does not even re-run this effect's body.
  //
  // latestVehicleRef holds the freshest `vehicle` for the fetch call itself (updated on
  // every render, no effect involved) -- deps alone can't supply this: a primitive
  // vehicle_id dep means the effect's own closure is pinned to whichever render last
  // changed a dep, which can be stale by however long routeDetail took to load after
  // the click. etaFetchedForVehicleRef is what makes "once per selection" literal
  // rather than "once per route": nextStop/destinationStop sit in the deps only so the
  // fetch can fire the FIRST time route detail finishes loading after a click (never
  // ready on the click's own first render) -- the ref guard stops a SECOND fetch from
  // firing later if the SAME selection's bus simply advances to its next stop while the
  // panel stays open. A fresh click on a different plate is the only thing that fires
  // another call.
  const latestVehicleRef = useRef<BusBoardRow | null>(null)
  latestVehicleRef.current = vehicle
  const etaFetchedForVehicleRef = useRef<string | null>(null)
  const [eta, setEta] = useState<BusRouteEta | null>(null)
  useEffect(() => {
    if (!vehicle || vehicle.vehicle_id !== etaFetchedForVehicleRef.current) {
      setEta(null)
    }
    if (!vehicle) {
      etaFetchedForVehicleRef.current = null
      return
    }
    if (!nextStop || !destinationStop) return
    if (etaFetchedForVehicleRef.current === vehicle.vehicle_id) return
    const current = latestVehicleRef.current
    if (!current || current.lat == null || current.lon == null) return
    etaFetchedForVehicleRef.current = vehicle.vehicle_id
    let alive = true
    fetchBusEta({
      originLat: current.lat, originLon: current.lon,
      destLat: destinationStop.lat, destLon: destinationStop.lon,
      waypointLat: nextStop.lat, waypointLon: nextStop.lon,
    })
      .then((e) => { if (alive) setEta(e) })
      .catch(() => { /* a stale/no ETA beats the panel crashing */ })
    return () => { alive = false }
  }, [vehicle?.vehicle_id, nextStop?.sequence_no, destinationStop?.sequence_no])

  // Fetched once per selection, not polled -- same convention BusOpsShell.tsx's own
  // selectedGeotags follows for MBMT. Only ever has rows for a RESOLVED duty (see
  // serving/app.py's get_bus_geotags docstring), but fetched for any vehicle_id --
  // an empty list for an unresolved duty is itself informative, not an error.
  const [geotags, setGeotags] = useState<BusGeotagEvent[]>([])
  useEffect(() => {
    setGeotags([])
    if (!vehicle) return
    let alive = true
    fetchBusGeotags(vehicle.vehicle_id)
      .then((events) => { if (alive) setGeotags(events) })
      .catch(() => { if (alive) setGeotags([]) })
    return () => { alive = false }
  }, [vehicle?.vehicle_id])

  if (!vehicle) {
    return (
      <Panel className="shrink-0 p-4">
        <EmptyState
          icon={<Radio size={18} />}
          title="Select a bus"
          body="Click a bus on the map or in the Fleet panel to see live route details."
        />
      </Panel>
    )
  }

  return (
    <Panel className="shrink-0 p-4">
      <p className={EYEBROW}>Selected bus</p>
      <h2 className="font-mono text-[18px] font-bold text-[#0F172A]">{vehicle.vehicle_id}</h2>
      <p className="mt-1 text-[12px] text-[#64748B]">
        {vehicle.schedule_code ?? 'no schedule_code'}
        {vehicle.home_depot_id ? ` · ${vehicle.home_depot_id}` : ' · home depot unknown'}
      </p>

      {resolution && !isResolved && (
        <p className="mt-2 rounded-[10px] bg-[#FFFBEB] px-2.5 py-2 text-[12px] text-[#92400E]">
          {STATUS_LABEL[resolution.status] ?? resolution.status}
        </p>
      )}

      {isResolved && !hasOutshed(vehicle.status) && (
        <p className="mt-2 rounded-[10px] bg-[#F8FAFC] px-2.5 py-2 text-[12px] text-[#64748B]">
          {vehicle.status === 'in_depot'
            ? 'Duty mapped — route will show once this bus leaves the depot.'
            : 'Duty mapped, but this bus has no recent signal — route not shown.'}
        </p>
      )}

      {resolvedRouteId && (
        <div className="mt-2 rounded-[10px] bg-[#F8FAFC] px-2.5 py-2 text-[12px] text-[#0F172A]">
          <p>
            <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">Route </span>
            <span className="font-semibold">{routeDetail?.display_name ?? resolvedRouteId}</span>
          </p>
          {resolution?.status === STATUS_RESOLVED_GEOMETRIC && (
            <p className="mt-0.5 text-[11px] text-[#92400E]">
              Direction inferred from this bus&apos;s live GPS track, not scheduled data.
            </p>
          )}
          {routeDetail && (
            <p className="mt-0.5 text-[11px] text-[#64748B]">{routeDetail.stops.length} stops</p>
          )}
          {nextStop && (
            <p className="mt-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-[#94A3B8]">Next stop </span>
              <span className="font-semibold animate-pulse">{nextStop.stop_name}</span>
            </p>
          )}
          {eta && (eta.next_stop_duration_s != null || eta.destination_duration_s != null) ? (
            <p className="mt-1 font-mono tabular-nums text-[11px] text-[#0369A1]">
              {eta.next_stop_duration_s != null
                ? `ETA next ~${Math.round(eta.next_stop_duration_s / 60)}m`
                : 'ETA next —'}
              {' · '}
              {eta.destination_duration_s != null
                ? `terminus ~${Math.round(eta.destination_duration_s / 60)}m`
                : 'terminus —'}
            </p>
          ) : eta?.error ? (
            <p className="mt-1 text-[11px] text-[#94A3B8]">ETA unavailable</p>
          ) : null}
        </div>
      )}

      {geotags.length > 0 && (
        <div className="mt-2">
          <p className={clsx(EYEBROW, 'mb-1')}>Today&apos;s geotags</p>
          <ul className="max-h-28 space-y-1 overflow-y-auto pr-1">
            {geotags.slice(-12).reverse().map((g, i) => (
              <li key={`${g.ts}-${i}`} className="flex gap-2 text-[11px] text-[#475569]">
                <span className="shrink-0 font-mono tabular-nums text-[#94A3B8]">
                  {new Date(g.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })}
                </span>
                <span className="min-w-0 truncate">{g.name || g.kind}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  )
}
